import { Link, useParams, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type AdminUserDetail,
  type AdminUserProject,
  type AdminUserStructure,
} from '../lib/api';
import { Card } from '../lib/ui';
import { useAuth } from '../lib/auth';

/**
 * Admin user-detail drill-down. Mirrors legacy
 * /metrics/user/userinformation/{user}: shows the user's profile, role,
 * counts, plus the projects and structures they own with quote-completed
 * status. Reachable from the Users tab on /admin.
 */
export function AdminUserDetailPage() {
  const { user } = useAuth();
  const { id: idStr } = useParams<{ id: string }>();
  const id = Number(idStr);
  const { hash } = useLocation();

  const isAdmin = !!(user?.roles?.includes('admin') || user?.roles?.includes('super-admin'));

  const detail = useQuery({
    queryKey: ['admin-user-detail', id],
    queryFn: () => api.get<AdminUserDetail>(`/api/v1/admin/users/${id}`),
    enabled: isAdmin && Number.isFinite(id),
  });
  const projects = useQuery({
    queryKey: ['admin-user-projects', id],
    queryFn: () => api.get<AdminUserProject[]>(`/api/v1/admin/users/${id}/projects`),
    enabled: isAdmin && Number.isFinite(id),
  });
  const structures = useQuery({
    queryKey: ['admin-user-structures', id],
    queryFn: () => api.get<AdminUserStructure[]>(`/api/v1/admin/users/${id}/structures`),
    enabled: isAdmin && Number.isFinite(id),
  });

  // Scroll to the projects/reports section when arrived via the Users-report
  // "User reports" action (`/admin/console/users/{id}#reports`).
  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, [hash, projects.data]);

  if (!isAdmin) {
    return <div className="container-fluid mt-3 text-muted">Not authorized.</div>;
  }
  if (detail.isLoading) {
    return <div className="container-fluid mt-3 text-muted">Loading…</div>;
  }
  if (detail.error || !detail.data) {
    return <div className="container-fluid mt-3"><div className="alert alert-danger">User not found.</div></div>;
  }

  const u = detail.data;

  return (
    <div className="container-fluid">
      <div className="row mt-3">
        <div className="col-12">
          <div className="page-title-box d-flex justify-content-between align-items-end">
            <div>
              <Link to="/admin/console" className="text-primary small">← Back to admin</Link>
              <h4 className="page-title mb-1">{u.name || '(no name)'}</h4>
              <p className="text-muted mb-0">{u.email ?? <em>no email</em>} · {u.roles.join(', ') || 'no roles'}</p>
            </div>
            <div className="text-end text-muted small">
              <div>id={u.id}</div>
              <div>{u.suspended ? `Suspended ${u.suspended}` : 'Active'}</div>
              <div>Logins: {u.loginCount}</div>
            </div>
          </div>

          <Card title="Profile">
            <dl className="row g-3 mb-0">
              <Field label="Company" value={u.company} />
              <Field label="Customer type" value={u.customerType} />
              <Field label="Market" value={u.market} />
              <Field label="Phone" value={u.phone} />
              <Field label="City" value={u.city} />
              <Field label="State" value={u.state} />
              <Field label="County" value={u.county} />
              <Field label="ZIP" value={u.zipCode} />
              <Field label="Country" value={u.country} />
              <Field label="Newsletter opt-in" value={u.optinNewsletter} />
              <Field label="Created" value={u.createdAt?.slice(0, 10) ?? null} />
              <Field label="Updated" value={u.updatedAt?.slice(0, 10) ?? null} />
            </dl>
          </Card>

          <div id="reports" style={{ scrollMarginTop: 80 }} />
          <Card title={`Projects (${u.projectCount})`}>
            {projects.isLoading ? (
              <p className="text-muted">Loading…</p>
            ) : !projects.data || projects.data.length === 0 ? (
              <p className="text-muted">No projects.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Location</th>
                      <th>Quote</th>
                      <th>Submitted</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.data.map((p) => (
                      <tr key={p.id}>
                        <td className="text-muted small">{p.id}</td>
                        <td><Link to={`/admin/projects/${p.id}`}>{p.name}</Link></td>
                        <td className="text-muted small">{[p.city, p.state].filter(Boolean).join(', ') || '—'}</td>
                        <td className="small">{p.quoteCompleted ?? <span className="text-muted">—</span>}</td>
                        <td className="small">{p.submitted ?? <span className="text-muted">—</span>}</td>
                        <td className="small">
                          {p.deliveredStatus ? <span className="badge bg-success">Delivered</span>
                            : p.completedStatus ? <span className="badge bg-info">Completed</span>
                            : <span className="badge bg-secondary">In progress</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title={`Structures (${u.structureCount})`}>
            {structures.isLoading ? (
              <p className="text-muted">Loading…</p>
            ) : !structures.data || structures.data.length === 0 ? (
              <p className="text-muted">No structures.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Quote</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {structures.data.map((s) => (
                      <tr key={s.id}>
                        <td className="text-muted small">{s.id}</td>
                        <td><Link to={`/admin/structures/${s.id}`}>{s.name}</Link></td>
                        <td className="small">{s.quoteCompleted ?? <span className="text-muted">—</span>}</td>
                        <td className="small">{s.createdAt?.slice(0, 10) ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="col-12 col-sm-6">
      <dt className="text-uppercase text-muted small">{label}</dt>
      <dd className="mb-0">{value || <span className="text-muted">—</span>}</dd>
    </div>
  );
}
